import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, ArrowLeft, Edit, Trash2, User, Calendar, FileText, Image as ImageIcon, X, ChevronLeft, ChevronRight, MapPin, Box, Phone, Users } from 'lucide-react';
import { CungVeDichAPI } from '../services/API';
import ProvincesService from '../services/ProvincesService';
import SystemHeader from './SystemHeader';
import Toast from './Toast';

const ShipmentReportDetail = ({ userInfo, accessToken, onLogout, totalPoints = 0 }) => {
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [previewImageIndex, setPreviewImageIndex] = useState(null);
  const [toast, setToast] = useState(null);
  const [locationNames, setLocationNames] = useState({
    city: '',
    district: '',
    ward: ''
  });

  // Get reportId from URL params
  const urlParams = new URLSearchParams(window.location.search);
  const reportId = urlParams.get('reportId');

  useEffect(() => {
    if (reportId && accessToken) {
      fetchReportDetail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId, accessToken]);

  const handlePrevImage = () => {
    if (previewImageIndex === null || !report?.attachmentUrls) return;
    setPreviewImageIndex((prev) => 
      prev > 0 ? prev - 1 : report.attachmentUrls.length - 1
    );
  };

  const handleNextImage = () => {
    if (previewImageIndex === null || !report?.attachmentUrls) return;
    setPreviewImageIndex((prev) => 
      prev < report.attachmentUrls.length - 1 ? prev + 1 : 0
    );
  };

  // Handle keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (previewImageIndex === null) return;
      
      if (e.key === 'Escape') {
        setPreviewImageIndex(null);
      } else if (e.key === 'ArrowLeft') {
        handlePrevImage();
      } else if (e.key === 'ArrowRight') {
        handleNextImage();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewImageIndex, report?.attachmentUrls?.length]);

  const fetchReportDetail = async () => {
    try {
      setLoading(true);
      setError('');
      
      if (!accessToken) {
        throw new Error('Không có token xác thực');
      }
      
      const result = await CungVeDichAPI.getShipmentReport(reportId, accessToken);
      
      if (result.success) {
        setReport(result.data);
        
        // Check if city/district/ward are IDs (numbers) or names (strings)
        // If they're already names, use them directly. If they're IDs, load names
        const needsLoading = result.data.city && !isNaN(result.data.city);
        
        if (needsLoading) {
          await loadLocationNames(result.data);
        } else {
          // API already returned names, use them directly
          setLocationNames({
            city: result.data.city || '',
            district: result.data.district || '',
            ward: result.data.ward || ''
          });
        }
      } else {
        throw new Error('Không thể lấy chi tiết báo cáo');
      }
    } catch (error) {
      console.error('Error fetching report detail:', error);
      setError(error.message || 'Có lỗi xảy ra khi tải chi tiết báo cáo');
    } finally {
      setLoading(false);
    }
  };

  const loadLocationNames = async (reportData) => {
    try {
      const names = { city: '', district: '', ward: '' };
      
      if (reportData.city) {
        const provinces = await ProvincesService.getProvinces();
        // Convert to string for comparison (API might return string or number)
        const cityCode = String(reportData.city);
        const province = provinces.find(p => String(p.code) === cityCode);
        if (province) {
          names.city = province.name;
          console.log('Loaded city name:', province.name);
        }
      }
      
      if (reportData.district && reportData.city) {
        const districts = await ProvincesService.getDistrictsByProvince(reportData.city);
        const districtCode = String(reportData.district);
        const district = districts.find(d => String(d.code) === districtCode);
        if (district) {
          names.district = district.name;
          console.log('Loaded district name:', district.name);
        }
      }
      
      if (reportData.ward && reportData.district) {
        const wards = await ProvincesService.getWardsByDistrict(reportData.district);
        const wardCode = String(reportData.ward);
        const ward = wards.find(w => String(w.code) === wardCode);
        if (ward) {
          names.ward = ward.name;
          console.log('Loaded ward name:', ward.name);
        }
      }
      
      console.log('Final location names:', names);
      setLocationNames(names);
    } catch (error) {
      console.error('Error loading location names:', error);
    }
  };

  const handleBack = () => {
    urlParams.delete('reportId');
    navigate(`/shipment-reports?${urlParams.toString()}`);
  };

  const handleEdit = () => {
    navigate(`/shipment-report-form?reportId=${reportId}&${urlParams.toString()}`);
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      const result = await CungVeDichAPI.deleteShipmentReport(reportId, accessToken);
      
      if (result.success) {
        // Show success toast
        setToast({
          message: '✅ Xóa phiếu trả hàng thành công!',
          type: 'success'
        });
        
        // Navigate back to list after a short delay
        setTimeout(() => {
          handleBack();
        }, 1500);
      } else {
        throw new Error('Không thể xóa phiếu trả hàng');
      }
    } catch (error) {
      console.error('Error deleting report:', error);
      setToast({
        message: error.message || 'Có lỗi xảy ra khi xóa phiếu trả hàng',
        type: 'error'
      });
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      1: { text: 'Chờ xử lý', bgColor: 'bg-yellow-100', textColor: 'text-yellow-800', borderColor: 'border-yellow-300' },
      2: { text: 'Đã xử lý', bgColor: 'bg-green-100', textColor: 'text-green-800', borderColor: 'border-green-300' },
      3: { text: 'Đã hủy', bgColor: 'bg-red-100', textColor: 'text-red-800', borderColor: 'border-red-300' }
    };
    return statusMap[status] || { text: 'Không xác định', bgColor: 'bg-gray-100', textColor: 'text-gray-800', borderColor: 'border-gray-300' };
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <SystemHeader 
          userInfo={userInfo}
          onLogout={onLogout}
          totalPoints={totalPoints}
        />
        <div className="max-w-6xl mx-auto p-4 md:p-6">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
              <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-2/3"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="min-h-screen bg-gray-50">
        <SystemHeader 
          userInfo={userInfo}
          onLogout={onLogout}
          totalPoints={totalPoints}
        />
        <div className="max-w-6xl mx-auto p-4 md:p-6">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error || 'Không tìm thấy báo cáo'}
          </div>
          <button
            onClick={handleBack}
            className="mt-4 flex items-center gap-2 text-blue-500 hover:text-blue-700"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Quay lại danh sách</span>
          </button>
        </div>
      </div>
    );
  }

  const status = getStatusBadge(report.status);
  const totalQuantity = report.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      
      <SystemHeader 
        userInfo={userInfo} 
        onLogout={onLogout} 
        totalPoints={totalPoints} 
      />
      
      <div className="max-w-6xl mx-auto p-4 md:p-6">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Quay lại danh sách</span>
          </button>
          
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-500 rounded-lg">
                <Package className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Trả hàng #{report.id}</h1>
                <span className={`inline-block mt-1 px-3 py-1 text-sm font-medium rounded-full border ${status.bgColor} ${status.textColor} ${status.borderColor}`}>
                  {status.text}
                </span>
              </div>
            </div>
            
            <div className="flex gap-2">
              {report.status === 1 ? (
                <button
                  onClick={handleEdit}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                >
                  <Edit className="w-5 h-5" />
                  <span>Chỉnh sửa</span>
                </button>
              ) : (
                <button
                  disabled
                  className="flex items-center gap-2 px-4 py-2 bg-gray-300 text-gray-500 rounded-lg cursor-not-allowed"
                  title="Chỉ có thể chỉnh sửa phiếu ở trạng thái 'Chờ xử lý'"
                >
                  <Edit className="w-5 h-5" />
                  <span>Chỉnh sửa</span>
                </button>
              )}
              
              {report.status === 1 ? (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                >
                  <Trash2 className="w-5 h-5" />
                  <span>Xóa</span>
                </button>
              ) : (
                <button
                  disabled
                  className="flex items-center gap-2 px-4 py-2 bg-gray-300 text-gray-500 rounded-lg cursor-not-allowed"
                  title="Chỉ có thể xóa phiếu ở trạng thái 'Chờ xử lý'"
                >
                  <Trash2 className="w-5 h-5" />
                  <span>Xóa</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Warning when cannot edit/delete */}
        {report.status !== 1 && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-orange-400 rounded-full flex items-center justify-center">
                <span className="text-white text-sm">ℹ️</span>
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">
                  Không thể chỉnh sửa hoặc xóa phiếu trả hàng này
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  Chỉ có thể chỉnh sửa và xóa phiếu ở trạng thái <span className="font-semibold">"Chờ xử lý"</span>. Phiếu này đang ở trạng thái <span className="font-semibold">"{status.text}"</span>.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Report Info Card */}
        <div className="bg-white rounded-lg shadow-sm p-3 md:p-6 mb-4 md:mb-6">
          <h2 className="text-xs md:text-base font-semibold text-gray-900 mb-2 md:mb-3">Thông tin trả hàng</h2>
          
          <div className="grid grid-cols-2 md:grid-cols-2 gap-2 md:gap-3">
            {/* Row 1: Người tạo | Team Thủ lĩnh */}
            <div className="flex items-start gap-1 py-0.5">
              <User className="w-3 h-3 text-gray-400 mt-0.5 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-[10px] text-gray-600 leading-tight">Người tạo</p>
                <p className="text-[11px] font-medium text-gray-900 leading-tight truncate">{report.user?.name || report.user?.userName || 'N/A'}</p>
              </div>
            </div>
            
            {report.user?.teamLeaderInfo && (
              <div className="flex items-start gap-1 py-0.5">
                <Users className="w-3 h-3 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] text-gray-600 leading-tight">Team Thủ lĩnh</p>
                  <p className="text-[11px] font-medium text-gray-900 leading-tight truncate">
                    {report.user.teamLeaderInfo.facebook || report.user.teamLeaderInfo.name || 'N/A'}
                  </p>
                </div>
              </div>
            )}
            
            {/* Row 2: Số điện thoại | Ngày tạo */}
            {report.user?.phone && (
              <div className="flex items-start gap-1 py-0.5">
                <Phone className="w-3 h-3 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] text-gray-600 leading-tight">Số điện thoại</p>
                  <p className="text-[11px] font-medium text-gray-900 leading-tight">{report.user.phone}</p>
                </div>
              </div>
            )}
            
            <div className="flex items-start gap-1 py-0.5">
              <Calendar className="w-3 h-3 text-gray-400 mt-0.5 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-[10px] text-gray-600 leading-tight">Ngày tạo</p>
                <p className="text-[11px] font-medium text-gray-900 leading-tight">
                  {new Date(report.createdAt).toLocaleString('vi-VN', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            </div>
            
            {/* Row 3: Tổng số sản phẩm | Cập nhật lần cuối */}
            <div className="flex items-start gap-1 py-0.5">
              <Package className="w-3 h-3 text-gray-400 mt-0.5 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-[10px] text-gray-600 leading-tight">Tổng số sản phẩm</p>
                <p className="text-[11px] font-medium text-gray-900 leading-tight">{report.items?.length || 0} sp ({totalQuantity} đv)</p>
              </div>
            </div>
            
            <div className="flex items-start gap-1 py-0.5">
              <Calendar className="w-3 h-3 text-gray-400 mt-0.5 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-[10px] text-gray-600 leading-tight">Cập nhật lần cuối</p>
                <p className="text-[11px] font-medium text-gray-900 leading-tight">
                  {new Date(report.updatedAt).toLocaleString('vi-VN', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            </div>
            
            {/* Row 4: Tổng số thùng hàng | Ngày gửi hàng */}
            {report.totalBoxes > 0 && (
              <div className="flex items-start gap-1 py-0.5">
                <Box className="w-3 h-3 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] text-gray-600 leading-tight">Tổng số thùng hàng</p>
                  <p className="text-[11px] font-medium text-gray-900 leading-tight">{report.totalBoxes} thùng</p>
                </div>
              </div>
            )}
            
            {report.shipmentDate && (
              <div className="flex items-start gap-1 py-0.5">
                <Calendar className="w-3 h-3 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] text-gray-600 leading-tight">Ngày gửi hàng</p>
                  <p className="text-[11px] font-medium text-gray-900 leading-tight">
                    {new Date(report.shipmentDate).toLocaleDateString('vi-VN', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </p>
                </div>
              </div>
            )}
            
            {/* Address - Full Address Display with Names (spans full width) */}
            {(report.address || report.city || report.district || report.ward || locationNames.city || locationNames.district || locationNames.ward) && (
              <div className="flex items-start gap-1 py-0.5 md:col-span-2">
                <MapPin className="w-3 h-3 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] text-gray-600 leading-tight mb-0.5">Địa chỉ</p>
                  <p className="text-[11px] font-medium text-gray-900 leading-tight break-words">
                    {(() => {
                      const addressParts = [];
                      if (report.address) addressParts.push(report.address);
                      if (locationNames.ward) addressParts.push(locationNames.ward);
                      if (locationNames.district) addressParts.push(locationNames.district);
                      if (locationNames.city) addressParts.push(locationNames.city);
                      
                      // Fallback to IDs if names not loaded yet
                      if (addressParts.length === 0 || (addressParts.length === 1 && addressParts[0] === report.address)) {
                        if (report.ward && !locationNames.ward) {
                          // Try to load ward name if not loaded
                          console.log('Ward ID found but name not loaded:', report.ward);
                        }
                        if (report.district && !locationNames.district) {
                          console.log('District ID found but name not loaded:', report.district);
                        }
                        if (report.city && !locationNames.city) {
                          console.log('City ID found but name not loaded:', report.city);
                        }
                      }
                      
                      return addressParts.length > 0 ? addressParts.join(', ') : (report.address || 'Chưa có địa chỉ');
                    })()}
                  </p>
                </div>
              </div>
            )}
          </div>
          
          {report.notes && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex items-start gap-2">
                <FileText className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-600 mb-1">Ghi chú</p>
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">{report.notes}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Products List */}
        <div className="bg-white rounded-lg shadow-sm p-3 md:p-4 mb-4 md:mb-6">
          <h2 className="text-sm font-semibold text-gray-900 mb-2">Danh sách sản phẩm</h2>
          
          <div className="space-y-2">
            {report.items && report.items.length > 0 ? (
              report.items.map((item) => (
                <div key={item.id} className="flex gap-2 p-2 bg-gray-50 rounded-lg border border-gray-200">
                  {item.product?.imageUrl && (
                    <img
                      src={item.product.imageUrl}
                      alt={item.product.name}
                      className="w-11 h-11 object-cover rounded-lg flex-shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0 flex items-center">
                    <h3 className="text-xs font-medium text-gray-900 line-clamp-2">{item.product?.name || 'N/A'}</h3>
                  </div>
                  <div className="text-right flex-shrink-0 flex flex-col justify-center">
                    <p className="text-[10px] text-gray-600 leading-tight">SL</p>
                    <p className="text-base font-bold text-blue-500 leading-tight">{item.quantity}</p>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-gray-500 py-6 text-xs">
                Không có sản phẩm nào
              </div>
            )}
          </div>
        </div>

        {/* Attachments */}
        {report.attachmentUrls && report.attachmentUrls.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <ImageIcon className="w-5 h-5 text-gray-700" />
                <h2 className="text-lg font-semibold text-gray-900">
                  Ảnh đính kèm
                </h2>
              </div>
              <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full">
                {report.attachmentUrls.length} ảnh
              </span>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {report.attachmentUrls.map((url, index) => (
                <div key={index} className="relative group">
                  <div className="relative aspect-square rounded-xl overflow-hidden bg-gray-100 border-2 border-gray-200 group-hover:border-blue-400 transition-all duration-200">
                    <img
                      src={url}
                      alt={`Ảnh ${index + 1}`}
                      className="w-full h-full object-cover cursor-pointer group-hover:scale-110 transition-transform duration-200"
                      onClick={() => setPreviewImageIndex(index)}
                    />
                    {/* Overlay on hover */}
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-200 flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        <button
                          type="button"
                          onClick={() => setPreviewImageIndex(index)}
                          className="p-2 bg-white rounded-lg text-blue-600 hover:bg-blue-50 transition-colors"
                          title="Xem ảnh"
                        >
                          <ImageIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                  {/* Image number badge */}
                  <div className="absolute top-2 left-2 px-2 py-0.5 bg-black bg-opacity-60 text-white text-xs font-medium rounded-full">
                    {index + 1}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Xác nhận xóa</h3>
            <p className="text-gray-600 mb-6">
              Bạn có chắc chắn muốn xóa báo cáo #{report.id}? Hành động này không thể hoàn tác.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Hủy
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50"
              >
                {deleting ? 'Đang xóa...' : 'Xóa'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Lightbox */}
      {previewImageIndex !== null && report?.attachmentUrls && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center p-4 z-50 animate-fade-in"
          onClick={() => setPreviewImageIndex(null)}
        >
          {/* Close button */}
          <button
            onClick={() => setPreviewImageIndex(null)}
            className="absolute top-4 right-4 p-3 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-full transition-all z-20 backdrop-blur-sm"
            title="Đóng (ESC)"
          >
            <X className="w-6 h-6" />
          </button>
          
          {/* Image counter */}
          <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-60 text-white px-4 py-2 rounded-full text-sm font-medium backdrop-blur-sm z-20">
            {previewImageIndex + 1} / {report.attachmentUrls.length}
          </div>
          
          {/* Previous button */}
          {report.attachmentUrls.length > 1 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handlePrevImage();
              }}
              className="absolute left-2 sm:left-4 top-1/2 transform -translate-y-1/2 p-3 sm:p-4 bg-black bg-opacity-50 hover:bg-opacity-70 text-white rounded-full transition-all z-20 backdrop-blur-sm shadow-xl hover:scale-110"
              title="Ảnh trước (←)"
            >
              <ChevronLeft className="w-6 h-6 sm:w-8 sm:h-8 stroke-[3]" />
            </button>
          )}
          
          {/* Next button */}
          {report.attachmentUrls.length > 1 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleNextImage();
              }}
              className="absolute right-2 sm:right-4 top-1/2 transform -translate-y-1/2 p-3 sm:p-4 bg-black bg-opacity-50 hover:bg-opacity-70 text-white rounded-full transition-all z-20 backdrop-blur-sm shadow-xl hover:scale-110"
              title="Ảnh tiếp theo (→)"
            >
              <ChevronRight className="w-6 h-6 sm:w-8 sm:h-8 stroke-[3]" />
            </button>
          )}
          
          {/* Image */}
          <div className="relative max-w-6xl max-h-[85vh] w-full h-full flex items-center justify-center">
            <img
              src={report.attachmentUrls[previewImageIndex]}
              alt={`Ảnh ${previewImageIndex + 1}`}
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          
          {/* Instructions */}
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-60 text-white px-4 py-2 rounded-full text-xs backdrop-blur-sm">
            <span className="hidden sm:inline">← → để điều hướng • </span>ESC hoặc click bên ngoài để đóng
          </div>
        </div>
      )}
    </div>
  );
};

export default ShipmentReportDetail;

